import { AgentChatImageAttachment } from './AgentChat.types';

/** Enough for a stack of set-list screenshots without flooding the model. */
export const AGENT_IMAGE_LIMIT = 8;

/** Question sent when screenshots arrive with nothing typed. */
export const AGENT_SCREENSHOT_DEFAULT_PROMPT =
  'Pull the song list out of these screenshots.';

// Claude reads images best at or under this edge; bigger only costs tokens.
const MAX_EDGE_PX = 1568;
const JPEG_QUALITY = 0.85;

export const isImageFile = (file: File): boolean =>
  file.type.startsWith('image/');

/** Image files from a picker, paste or drop, ignoring everything else. */
export const imageFilesFrom = (
  items: FileList | File[] | null | undefined,
): File[] => Array.from(items ?? []).filter(isImageFile);

export const attachmentSrc = (attachment: AgentChatImageAttachment): string =>
  `data:${attachment.mediaType};base64,${attachment.data}`;

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Could not read ${file.name || 'image'}.`));
    };
    image.src = url;
  });

/** Shrinks and re-encodes one screenshot so it travels light over IPC. */
const toAttachment = async (file: File): Promise<AgentChatImageAttachment> => {
  const image = await loadImage(file);
  const longestEdge = Math.max(image.naturalWidth, image.naturalHeight, 1);
  const scale = Math.min(1, MAX_EDGE_PX / longestEdge);
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare the image.');
  // Handwritten lists photographed on a phone often have transparency-free
  // JPEG sources anyway; flattening onto white keeps PNG screenshots readable.
  context.fillStyle = '#fff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  return {
    id: crypto.randomUUID(),
    mediaType: 'image/jpeg',
    data: dataUrl.slice(dataUrl.indexOf(',') + 1),
  };
};

/**
 * Converts up to `room` files, skipping any that will not decode (e.g. HEIC)
 * rather than failing the whole batch.
 */
export const attachmentsFromFiles = async (
  files: File[],
  room: number,
): Promise<AgentChatImageAttachment[]> => {
  const selected = files.slice(0, Math.max(0, room));
  const settled = await Promise.allSettled(selected.map(toAttachment));
  return settled
    .filter(
      (entry): entry is PromiseFulfilledResult<AgentChatImageAttachment> =>
        entry.status === 'fulfilled',
    )
    .map((entry) => entry.value);
};
